// src/App.js
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Link } from 'react-router-dom';
import './App.css';

// ── Eager CSS bundle (prevents FOUC on hard-refresh of lazy routes) ──────────
// Vite splits CSS from lazy chunks and injects it via JS, so on a hard refresh
// the component renders before its CSS arrives. Importing all page/component
// CSS here forces Vite to include them in the initial HTML <link> bundle.
import './Pages/Dashboard/Dashboard.css';
import './Pages/Party/Party.css';
import './Pages/Product/Product.css';
import './Pages/Service/Service.css';
import './Pages/CashBankAccount/CashBankAccount.css';
import './Pages/SalesQuotes/Sales.css';
import './Pages/SalesInvoices/SalesInvoices.css';
import './Pages/SalesNote/SalesNote.css';
import './Pages/PurchaseInvoice/PurchaseInvoice.css';
import './Pages/PurchaseOrder/PurchaseOrder.css';
import './Pages/PurchaseNote/PurchaseNote.css';
import './Pages/Report/Reports.css';
import './Pages/Settings/Settings.css';
import './Pages/GSTFiling/GSTFiling.css';
import './Pages/GSTFiling/GSTFilingDetail.css';
import './Pages/GSTFiling/gstr1/Gstr1Dashboard.css';
import './Pages/Onboarding/Onboarding.css';
import './Pages/CA/CAClientList/CAClientList.css';
import './Pages/CA/CAPracticeDashboard/CAPracticeDashboard.css';
import './Components/Common/Sidebar/Sidebar.css';
import './Components/Common/Modal/Modal.css';
import './Components/Common/FormModal/FormModal.css';
import './Components/Common/DataTable/DataTable.css';
import './Components/Common/DataCard/DataCard.css';
import './Components/Common/Filter/Filter.css';
import './Components/Common/ResponsiveFilter/ResponsiveFilter.css';
import './Components/Common/FieldManager/FieldManager.css';
import './Components/Common/InvoiceFlag/InvoiceFlag.css';
import './Components/Common/StatusBadge/StatusBadge.css';
import './Components/Common/Combobox/Combobox.css';
import './Components/Common/PaginationControls/PaginationControls.css';
import './Components/Common/WorkspaceBanner/WorkspaceBanner.css';
import './Components/Common/TopHeader/TopHeader.css';
import './Components/Common/ImportMapping/ImportMappingModal.css';
import './Components/Common/SalesDetailPage/SalesDetailPage.css';
import './Components/Common/PurchaseDetailPage/PurchaseDetailPage.css';
import './Components/Common/PartyDetailPage/PartyDetailPage.css';
import './Components/Common/ProductDetailPage/ProductDetailPage.css';
import './Components/Common/ServiceDetailPage/ServiceDetailPage.css';
import './Components/Common/InvoiceTemplateDesigner/InvoiceTemplateDesigner.css';
import './Components/Common/InvoiceFieldManager/InvoiceFieldManager.css';
// ─────────────────────────────────────────────────────────────────────────────

// Import the AuthProvider from your context file
import { AuthProvider, useAuth } from './Context/AuthContext';
import { registerSubscriptionExpiredHandler } from './utils/apiFetch';
// Import the PrivateRoute component (you'll create this next)
import PrivateRoute from './Context/PrivateRoute';
import CARoute from './Context/CARoute';
import { NotificationProvider } from './Context/NotificationContext';
import TopHeader from './Components/Common/TopHeader';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ── Eager-loaded (shown before auth resolves — keep small) ──────────────────
import Home    from './Pages/Home';
import Login   from './Pages/Login';
import Register from './Pages/Register';
import FreeTrial from './Pages/FreeTrial';
import Onboarding from './Pages/Onboarding';
import ErrorBoundary from './Components/Common/ErrorBoundary';
import OfflineBanner from './Components/Common/OfflineBanner';

// ── Lazy-loaded routes (split into separate chunks by webpack) ──────────────
const Dashboard               = lazy(() => import('./Pages/Dashboard'));
const Party                   = lazy(() => import('./Pages/Party'));
const Product                 = lazy(() => import('./Pages/Product'));
const Service                 = lazy(() => import('./Pages/Service'));
const CashBankAccountPage     = lazy(() => import('./Pages/CashBankAccount'));
const SalesInvoices           = lazy(() => import('./Pages/SalesInvoices'));
const QuoteListPage           = lazy(() => import('./Pages/SalesQuotes'));
const SalesQuoteFormPage      = lazy(() => import('./Pages/SalesQuotes/Form'));
const SalesInvoiceFormPage    = lazy(() => import('./Pages/SalesInvoices/Form'));
const PurchaseInvoices        = lazy(() => import('./Pages/PurchaseInvoice'));
const PurchaseInvoiceFormPage = lazy(() => import('./Pages/PurchaseInvoice/Form'));
const PurchaseOrderFormPage   = lazy(() => import('./Pages/PurchaseOrder/Form'));
const PurchaseOrderListPage   = lazy(() => import('./Pages/PurchaseOrder'));
const SalesNotePage           = lazy(() => import('./Pages/SalesNote'));
const SalesNoteFormPage       = lazy(() => import('./Pages/SalesNote/Form'));
const PurchaseNotePage        = lazy(() => import('./Pages/PurchaseNote'));
const PurchaseNoteFormPage    = lazy(() => import('./Pages/PurchaseNote/Form'));
const Reports                 = lazy(() => import('./Pages/Report'));
const ReportView              = lazy(() => import('./Pages/Report/View'));
const Settings                = lazy(() => import('./Pages/Settings'));
const SettingsProfile         = lazy(() => import('./Pages/Settings/SettingsProfile'));
const SettingsConfiguration   = lazy(() => import('./Pages/Settings/SettingsConfiguration'));
const SessionManagement       = lazy(() => import('./Pages/Settings/SessionManagement'));
const SettingsSubscription    = lazy(() => import('./Pages/Settings/SettingsSubscription'));
const SettingsSupport         = lazy(() => import('./Pages/Settings/SettingsSupport'));
const SettingsIntegrations    = lazy(() => import('./Pages/Settings/SettingsIntegrations'));
const DashboardList           = lazy(() => import('./Pages/Settings/DashboardList'));
const DashboardBuilder        = lazy(() => import('./Pages/Settings/DashboardBuilder'));
const TemplateList            = lazy(() => import('./Pages/Settings/TemplateList'));
const GSTFiling               = lazy(() => import('./Pages/GSTFiling'));
const GSTFilingDetail         = lazy(() => import('./Pages/GSTFiling/Detail'));
const PartyDetailPage         = lazy(() => import('./Components/Common/PartyDetailPage').then(m => ({ default: m.PartyDetailPage })));
const ProductDetailPage       = lazy(() => import('./Components/Common/ProductDetailPage').then(m => ({ default: m.ProductDetailPage })));
const ServiceDetailPage       = lazy(() => import('./Components/Common/ServiceDetailPage').then(m => ({ default: m.ServiceDetailPage })));
const InvoiceTemplateDesigner = lazy(() => import('./Components/Common/InvoiceTemplateDesigner'));
const InvoiceFieldManager     = lazy(() => import('./Components/Common/InvoiceFieldManager'));
const SalesDetailPage         = lazy(() => import('./Components/Common/SalesDetailPage'));
const PurchaseDetailPage      = lazy(() => import('./Components/Common/PurchaseDetailPage'));
const CAClientList            = lazy(() => import('./Pages/CA/CAClientList'));
const CAPracticeDashboard     = lazy(() => import('./Pages/CA/CAPracticeDashboard'));
const CAClientWorkspace       = lazy(() => import('./Pages/CA/CAClientWorkspace'));

function AuthenticatedHeader() {
  const { isAuthenticated, loadingAuth, isSubscriptionExpired: isExpired } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    registerSubscriptionExpiredHandler(() => navigate('/settings/subscription'));
  }, [navigate]);

  if (!isAuthenticated || loadingAuth) return null;

  return (
    <>
      <TopHeader />
      {isExpired && (
        <div className="sub-expired-banner">
          Your subscription has expired.{' '}
          <Link to="/settings/subscription">Renew now</Link>
          {' '}to restore full access.
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <div>
      <Router>
        <AuthProvider>
          <NotificationProvider>
          <AuthenticatedHeader />
          <OfflineBanner />
          <div className="App">
            <Suspense fallback={<div className="route-loading" />}>
              <Routes>
                <Route path="/login"       element={<Login />} />
                <Route path="/free-trial"  element={<FreeTrial />} />
                <Route path="/register"    element={<Register />} />
                <Route path="/onboarding"  element={<Onboarding />} />

                {/* Protected Routes */}
                <Route path="/"            element={<PrivateRoute><Navigate to="/dashboard" replace /></PrivateRoute>} />

                <Route path="/dashboard" element={
                  <PrivateRoute><ErrorBoundary><Dashboard /></ErrorBoundary></PrivateRoute>
                } />
                <Route path="/party"   element={<PrivateRoute><Party /></PrivateRoute>} />
                <Route path="/product" element={<PrivateRoute><Product /></PrivateRoute>} />
                <Route path="/service" element={<PrivateRoute><Service /></PrivateRoute>} />
                <Route path="/account" element={<PrivateRoute><CashBankAccountPage /></PrivateRoute>} />

                <Route path="/sales/quotes"          element={<PrivateRoute><QuoteListPage /></PrivateRoute>} />
                <Route path="/sales/quotes/new"      element={<PrivateRoute><SalesQuoteFormPage /></PrivateRoute>} />
                <Route path="/sales/quotes/edit/:id" element={<PrivateRoute><SalesQuoteFormPage /></PrivateRoute>} />
                <Route path="/sales/quotes/view/:id" element={<PrivateRoute><SalesDetailPage /></PrivateRoute>} />

                <Route path="/sales/invoice"          element={<PrivateRoute><ErrorBoundary><SalesInvoices /></ErrorBoundary></PrivateRoute>} />
                <Route path="/sales/invoice/new"      element={<PrivateRoute><SalesInvoiceFormPage /></PrivateRoute>} />
                <Route path="/sales/invoice/edit/:id" element={<PrivateRoute><SalesInvoiceFormPage /></PrivateRoute>} />
                <Route path="/sales/invoice/view/:id" element={<PrivateRoute><SalesDetailPage /></PrivateRoute>} />

                <Route path="/sales/note"          element={<PrivateRoute><SalesNotePage /></PrivateRoute>} />
                <Route path="/sales/note/edit/:id" element={<PrivateRoute><SalesNoteFormPage /></PrivateRoute>} />
                <Route path="/sales/note/view/:id" element={<PrivateRoute><SalesDetailPage /></PrivateRoute>} />

                <Route path="/purchase/orders"          element={<PrivateRoute><PurchaseOrderListPage /></PrivateRoute>} />
                <Route path="/purchase/order/new"       element={<PrivateRoute><PurchaseOrderFormPage /></PrivateRoute>} />
                <Route path="/purchase/order/edit/:id"  element={<PrivateRoute><PurchaseOrderFormPage /></PrivateRoute>} />
                <Route path="/purchase/order/view/:id"  element={<PrivateRoute><PurchaseDetailPage /></PrivateRoute>} />

                <Route path="/purchase/invoice"          element={<PrivateRoute><ErrorBoundary><PurchaseInvoices /></ErrorBoundary></PrivateRoute>} />
                <Route path="/purchase/invoice/new"      element={<PrivateRoute><PurchaseInvoiceFormPage /></PrivateRoute>} />
                <Route path="/purchase/invoice/edit/:id" element={<PrivateRoute><PurchaseInvoiceFormPage /></PrivateRoute>} />
                <Route path="/purchase/invoice/view/:id" element={<PrivateRoute><PurchaseDetailPage /></PrivateRoute>} />

                <Route path="/purchase/note"          element={<PrivateRoute><PurchaseNotePage /></PrivateRoute>} />
                <Route path="/purchase/note/edit/:id" element={<PrivateRoute><PurchaseNoteFormPage /></PrivateRoute>} />
                <Route path="/purchase/note/view/:id" element={<PrivateRoute><PurchaseDetailPage /></PrivateRoute>} />

                <Route path="/reports"          element={<PrivateRoute><ErrorBoundary><Reports /></ErrorBoundary></PrivateRoute>} />
                <Route path="/reports/view/:id" element={<PrivateRoute><ReportView /></PrivateRoute>} />

                <Route path="/gst-filing"        element={<PrivateRoute><ErrorBoundary><GSTFiling /></ErrorBoundary></PrivateRoute>} />
                <Route path="/gst-filing/:form"  element={<PrivateRoute><ErrorBoundary><GSTFilingDetail /></ErrorBoundary></PrivateRoute>} />

                <Route path="/settings"                    element={<PrivateRoute><Settings /></PrivateRoute>} />
                <Route path="/settings/profile"            element={<PrivateRoute><SettingsProfile /></PrivateRoute>} />
                <Route path="/settings/configuration"      element={<PrivateRoute><SettingsConfiguration /></PrivateRoute>} />
                <Route path="/settings/sessions"           element={<PrivateRoute><SessionManagement /></PrivateRoute>} />
                <Route path="/settings/subscription"       element={<PrivateRoute><SettingsSubscription /></PrivateRoute>} />
                <Route path="/settings/support"            element={<PrivateRoute><SettingsSupport /></PrivateRoute>} />
                <Route path="/settings/integrations"       element={<PrivateRoute><SettingsIntegrations /></PrivateRoute>} />
                <Route path="/settings/dashboards"         element={<PrivateRoute><DashboardList /></PrivateRoute>} />
                <Route path="/settings/dashboards/new"     element={<PrivateRoute><DashboardBuilder /></PrivateRoute>} />
                <Route path="/settings/dashboards/:id"     element={<PrivateRoute><DashboardBuilder /></PrivateRoute>} />
                <Route path="/settings/templates"          element={<PrivateRoute><TemplateList /></PrivateRoute>} />

                <Route path="/invoice-field-manager"           element={<PrivateRoute><InvoiceFieldManager /></PrivateRoute>} />
                <Route path="/invoice-template-designer"       element={<PrivateRoute><InvoiceTemplateDesigner /></PrivateRoute>} />
                <Route path="/invoice-template-designer/:id"   element={<PrivateRoute><InvoiceTemplateDesigner /></PrivateRoute>} />

                <Route path="/PartyDetails"   element={<PrivateRoute><PartyDetailPage /></PrivateRoute>} />
                <Route path="/ProductDetails" element={<PrivateRoute><ProductDetailPage /></PrivateRoute>} />
                <Route path="/ServiceDetails" element={<PrivateRoute><ServiceDetailPage /></PrivateRoute>} />

                {/* CA Routes */}
                <Route path="/ca/dashboard" element={<CARoute><CAPracticeDashboard /></CARoute>} />
                <Route path="/ca/clients"   element={<CARoute><CAClientList /></CARoute>} />

                {/* CA Client Workspace — nested routes proxy all pages to the client's data */}
                <Route path="/ca/clients/:id/workspace" element={<CARoute><CAClientWorkspace /></CARoute>}>
                  <Route path="dashboard"                       element={<Dashboard />} />
                  <Route path="party"                           element={<Party />} />
                  <Route path="product"                         element={<Product />} />
                  <Route path="service"                         element={<Service />} />
                  <Route path="account"                         element={<CashBankAccountPage />} />
                  <Route path="sales/quotes"                    element={<QuoteListPage />} />
                  <Route path="sales/quotes/new"                element={<SalesQuoteFormPage />} />
                  <Route path="sales/quotes/edit/:invoiceId"    element={<SalesQuoteFormPage />} />
                  <Route path="sales/quotes/view/:invoiceId"    element={<SalesDetailPage />} />
                  <Route path="sales/invoice"                   element={<SalesInvoices />} />
                  <Route path="sales/invoice/new"               element={<SalesInvoiceFormPage />} />
                  <Route path="sales/invoice/edit/:invoiceId"   element={<SalesInvoiceFormPage />} />
                  <Route path="sales/invoice/view/:invoiceId"   element={<SalesDetailPage />} />
                  <Route path="sales/note"                      element={<SalesNotePage />} />
                  <Route path="sales/note/edit/:invoiceId"      element={<SalesNoteFormPage />} />
                  <Route path="sales/note/view/:invoiceId"      element={<SalesDetailPage />} />
                  <Route path="purchase/orders"                 element={<PurchaseOrderListPage />} />
                  <Route path="purchase/order/new"              element={<PurchaseOrderFormPage />} />
                  <Route path="purchase/order/edit/:invoiceId"  element={<PurchaseOrderFormPage />} />
                  <Route path="purchase/order/view/:invoiceId"  element={<PurchaseDetailPage />} />
                  <Route path="purchase/invoice"                element={<PurchaseInvoices />} />
                  <Route path="purchase/invoice/new"            element={<PurchaseInvoiceFormPage />} />
                  <Route path="purchase/invoice/edit/:invoiceId" element={<PurchaseInvoiceFormPage />} />
                  <Route path="purchase/invoice/view/:invoiceId" element={<PurchaseDetailPage />} />
                  <Route path="purchase/note"                   element={<PurchaseNotePage />} />
                  <Route path="purchase/note/edit/:invoiceId"   element={<PurchaseNoteFormPage />} />
                  <Route path="purchase/note/view/:invoiceId"   element={<PurchaseDetailPage />} />
                  <Route path="gst-filing"                      element={<GSTFiling />} />
                  <Route path="gst-filing/:form"                element={<GSTFilingDetail />} />
                  <Route path="reports"                         element={<Reports />} />
                  <Route path="reports/view/:reportId"          element={<ReportView />} />
                  <Route path="PartyDetails"                    element={<PartyDetailPage />} />
                  <Route path="ProductDetails"                  element={<ProductDetailPage />} />
                  <Route path="ServiceDetails"                  element={<ServiceDetailPage />} />
                </Route>
              </Routes>
            </Suspense>
          </div>
          </NotificationProvider>
        </AuthProvider>
      </Router>
      <ToastContainer />
    </div>
  );
}

export default App;
